import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { VideoBlockItem, type VideoRenderer } from "./video-block-item";

/**
 * The block node a Mux-hosted video occupies in a document. An atom: the video is one
 * unit the caret steps over, carrying only the id the renderer resolves and the title.
 *
 * The node must sit in the shared extension list of `useTiptap` even where no renderer is
 * passed — `generateHTML` throws on any node type its extension list does not know, and the
 * catch around it falls back to showing the raw JSON string. One list serves every call
 * site, so the node ships everywhere and only the renderer is optional.
 *
 * @param videoRenderer - Resolves a video id to the embedded player; absent, the node view
 * renders a labelled placeholder.
 * @returns The configured extension for the editor's extension list.
 */
export function buildVideoBlock(videoRenderer?: VideoRenderer) {
	return Node.create({
		name: "videoBlock",
		group: "block",
		atom: true,
		draggable: true,

		addAttributes() {
			return {
				videoId: { default: null },
			};
		},

		parseHTML() {
			return [
				{
					tag: "video-block[data-video-id]",
					getAttrs: (element) => ({
						videoId: element.getAttribute("data-video-id"),
					}),
				},
			];
		},

		renderHTML({ node, HTMLAttributes }) {
			return [
				"video-block",
				mergeAttributes(
					{
						"data-video-id": node.attrs.videoId,
					},
					HTMLAttributes,
				),
			];
		},

		addNodeView() {
			return ReactNodeViewRenderer((props) => (
				<VideoBlockItem
					{...props}
					videoRenderer={videoRenderer}
				/>
			));
		},
	});
}
